import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import ApiURL from "../Api/Api";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "./SidebarContext";
import TransactionProofImage from "./TransactionProofImage";

const AdminReturnRequestReport = () => {
  const [returnRequests, setReturnRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const navigate = useNavigate();
  const user_id = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const canManage = role === "admin" || role === "company_owner";
  const requesterLabel = role === "company_owner" ? "Admin" : "Worker";
  const pageTitle = role === "company_owner" ? "Admin Return Request Report" : "Worker Return Request Report";

  const fetchReturnRequests = async () => {
    if (!user_id || !role || !["worker", "admin", "company_owner"].includes(role)) {
      console.error('Invalid credentials:', { user_id, role });
      setError("Access denied");
      toast.error("Access denied");
      return [];
    }

    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getReturnRequests.php`);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("role", role);
      console.log('Fetching URL:', url.toString());

      const response = await fetch(url.toString());
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      const data = await response.json();

      console.log('Fetched return requests:', JSON.stringify(data, null, 2));

      if (data.status === "success") {
        const sortedRequests = (data.requests || []).sort((a, b) => {
          return parseInt(a.id) - parseInt(b.id); // Sort by Request ID ascending
        });
        let runningBalance = 0;
        const requestsWithCumulative = sortedRequests.map((req) => {
          runningBalance += parseFloat(req.total_return_amount || 0);
          return { ...req, cumulative_balance: runningBalance };
        });
        setReturnRequests(requestsWithCumulative);
        setError(null);
        return requestsWithCumulative;
      } else {
        throw new Error(data.message || "Failed to load return requests");
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("Failed to fetch return requests: " + err.message);
      toast.error("Failed to fetch return requests: " + err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (requestId, action, reason = "") => {
    if (!canManage) return;
    setShowConfirmModal({ requestId, action, reason });
  };

  const confirmAction = async () => {
    if (!canManage) {
      setShowConfirmModal(null);
      return;
    }
    const { requestId, action, reason } = showConfirmModal;
    try {
      const response = await fetch(`${ApiURL}/manageReturnRequest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: requestId,
          action,
          rejection_reason: reason,
          admin_id: parseInt(user_id),
        }),
      });

      console.log('Action payload:', JSON.stringify({ request_id: requestId, action, rejection_reason: reason, admin_id: parseInt(user_id) }, null, 2));

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (parseErr) {
        console.warn('manageReturnRequest returned non-JSON response:', text, parseErr);
      }

      if (!response.ok) {
        const errorMessage = data?.message || `Server error (${response.status})${text ? `: ${text}` : ''}`;

        // If the backend returned an empty 500 but the request actually updated,
        // refresh and infer success from the request state.
        if (!text) {
          const updatedRequests = await fetchReturnRequests();
          const updatedRequest = updatedRequests.find(
            (req) => req.id?.toString() === requestId.toString()
          );
          if (
            updatedRequest &&
            ((action === 'approve' && updatedRequest.status === 'approved') ||
              (action === 'reject' && updatedRequest.status === 'rejected'))
          ) {
            toast.success(`Request ${action}d successfully`);
            setSelectedRequest(null);
            setRejectionReason("");
            setShowConfirmModal(null);
            setExpandedRequest(null);
            return;
          }
        }

        throw new Error(errorMessage);
      }

      if (data?.status === 'success') {
        toast.success(`Request ${action}d successfully`);
        await fetchReturnRequests();
        setSelectedRequest(null);
        setRejectionReason("");
        setShowConfirmModal(null);
        setExpandedRequest(null);
      } else {
        throw new Error(data?.message || `Failed to ${action} request`);
      }
    } catch (err) {
      console.error('Action error:', err);
      toast.error(`Failed to ${action} request: ` + err.message);
    }
  };

  const toggleDetails = (requestId) => {
    console.log('Toggling details for request:', requestId);
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  const exportToCSV = () => {
    if (returnRequests.length === 0) {
      toast.warn("No return requests to export.");
      return;
    }

    const headers = [
      "Request ID",
      "Worker",
      "Date",
      "Amount",
      "Cumulative Balance",
      "Particulars",
      "Site",
      "Main Head",
      "Sub Head",
      "Purpose",
      "Status",
      "Rejection Reason",
      "Created By",
      "Created At",
      "Updated By",
      "Updated At",
      "Return Type",
      "Transaction Count",
      "Transaction Nos",
      "Transaction Created By",
    ];

    const rows = returnRequests.map((req) => [
      `"${req.id}"`,
      `"${req.requester_name || req.worker_name || "-"}"`,
      `"${req.transactions[0]?.transaction_date ? formatDate(req.transactions[0].transaction_date) : "-"}"`,
      `"${formatCurrency(req.total_return_amount)}"`,
      `"${formatCurrency(req.cumulative_balance)}"`,
      `"${req.transactions.map(t => t.particuler_to || "-").join("; ")}"`,
      `"${req.transactions.map(t => t.site || "-").join("; ")}"`,
      `"${req.transactions.map(t => t.main_head || "-").join("; ")}"`,
      `"${req.transactions.map(t => t.sub_head || "-").join("; ")}"`,
      `"${req.purpose || "-"}"`,
      `"${req.status || "-"}"`,
      `"${req.rejection_reason || "-"}"`,
      `"${req.created_by_name || "-"}"`,
      `"${formatDateTime(req.created_at)}"`,
      `"${req.updated_by_name || "-"}"`,
      `"${req.updated_at ? formatDateTime(req.updated_at) : "-"}"`,
      `"${req.return_type || "-"}"`,
      `"${req.transaction_count || 0}"`,
      `"${req.transactions.map(t => t.transaction_no).join("; ")}"`,
      `"${req.transactions.map(t => t.transaction_created_by_name || req.created_by_name || "-").join("; ")}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `return_request_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchReturnRequests();
  }, []);

  const formatCurrency = (value) => {
    return value != null ? `₹${Number(value).toLocaleString("en-IN")}` : "₹0";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).replace(/\//g, "/");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).replace(/,/, "");
  };

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className={`min-w-0 flex-1 min-h-screen transition-all duration-300 ${
            isSidebarOpen ? "lg:ml-72" : ""
          } ${isSidebarOpen && screenWidth < 640 ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6">
          <div className="flex items-center justify-between mb-3 sm:mb-5 gap-2">
            <div>
              <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">{pageTitle}</h1>
              <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">{returnRequests.length} return requests</p>
            </div>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={isLoading || returnRequests.length === 0}
            >
              Export CSV
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-3 text-red-500 text-xs sm:text-sm"
            >
              {error}
            </motion.div>
          )}

          {isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-6 text-gray-500 animate-pulse text-xs sm:text-sm"
            >
              Loading return requests...
            </motion.div>
          ) : returnRequests.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-6 text-gray-500 text-xs sm:text-sm"
            >
              No return requests found
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
            >
              <div className="overflow-x-auto">
              <table className="w-full text-[12px] text-left border-collapse min-w-[1450px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    {[
                      "Request ID", requesterLabel, "Date", "Amount", "Particulars", "Site",
                      "Main Head", "Sub Head", "Purpose", "Proof", "Status", "Rejection Reason",
                      "Created By", "Created At", "Updated By", "Updated At", "Return Type",
                      ...(canManage ? ["Action"] : []),
                    ].map((header) => (
                      <th key={header} className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {returnRequests.map((req) => (
                    <React.Fragment key={req.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`hover:bg-slate-50/60 transition-colors ${
                          req.status === "pending"
                            ? "bg-yellow-50"
                            : req.status === "approved"
                            ? "bg-green-50"
                            : "bg-red-50"
                        }`}
                      >
                        <td className="px-3 py-2.5 text-slate-400 font-medium">{req.id}</td>
                        <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{req.requester_name || req.worker_name || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{req.transactions[0]?.transaction_date ? formatDate(req.transactions[0].transaction_date) : "-"}</td>
                        <td className="px-3 py-2.5 text-slate-700 font-bold whitespace-nowrap">{formatCurrency(req.total_return_amount)}</td>
                        <td className="px-3 py-2.5 text-slate-700 max-w-[190px] whitespace-normal break-words leading-snug">
                          {(req.transaction_count || 0) > 1 ? (
                            <button
                              onClick={() => toggleDetails(req.id)}
                              className="text-red-600 hover:text-red-700 text-xs font-semibold"
                            >
                              View {req.transaction_count || 0} Transactions
                            </button>
                          ) : (
                            req.transactions[0]?.particuler_to || "-"
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{req.transactions[0]?.site || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{req.transactions[0]?.main_head || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{req.transactions[0]?.sub_head || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-500 max-w-[170px] whitespace-normal break-words leading-snug">{req.purpose || "-"}</td>
                        <td className="px-3 py-2.5"><TransactionProofImage path={req.attachment_path || req.transactions[0]?.attachment_path} /></td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`px-1 py-0.5 rounded-full text-[10px] ${
                              req.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : req.status === "approved"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {req.status || "-"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 max-w-[170px] whitespace-normal break-words leading-snug">{req.rejection_reason || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{req.created_by_name || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-400 text-[11px] whitespace-nowrap">{formatDateTime(req.created_at)}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{req.updated_by_name || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-400 text-[11px] whitespace-nowrap">{req.updated_at ? formatDateTime(req.updated_at) : "-"}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{req.return_type || "-"}</td>
                        {canManage && req.approval_role === "approver" && (
                          <td className="px-3 py-2.5">
                            {req.status === "pending" ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAction(req.id, "approve")}
                                  className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold rounded-lg hover:bg-emerald-100 whitespace-nowrap"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => setSelectedRequest(req.id)}
                                  className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold rounded-lg hover:bg-red-100 whitespace-nowrap"
                                >
                                  Reject
                                </button>
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                      </motion.tr>
                      {(req.transaction_count || 0) > 1 && expandedRequest === req.id && (
                        <motion.tr
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.3 }}
                          className="bg-gray-50"
                        >
                          <td colSpan={canManage ? 18 : 17} className="px-1 py-1.5">
                            <div className="pl-4">
                              <h4 className="text-xs font-semibold mb-2">Transaction Details</h4>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-200">
                                    <th className="px-1 py-1">Transaction No</th>
                                    <th className="px-1 py-1">Amount</th>
                                    <th className="px-1 py-1">Date</th>
                                    <th className="px-1 py-1">Particulars</th>
                                    <th className="px-1 py-1">Site</th>
                                    <th className="px-1 py-1">Main Head</th>
                                    <th className="px-1 py-1">Sub Head</th>
                                    <th className="px-1 py-1">Proof</th>
                                    <th className="px-1 py-1">Created At</th>
                                    <th className="px-1 py-1">Created By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(req.transactions || []).map((txn, txnIndex) => (
                                    <tr key={`${txn.transaction_no}-${txnIndex}`} className="border-b">
                                      <td className="px-1 py-1">{txn.transaction_no || "-"}</td>
                                      <td className="px-1 py-1">{formatCurrency(txn.return_amount)}</td>
                                      <td className="px-1 py-1">{formatDate(txn.transaction_date)}</td>
                                      <td className="px-1 py-1">{txn.particuler_to || "-"}</td>
                                      <td className="px-1 py-1">{txn.site || "-"}</td>
                                      <td className="px-1 py-1">{txn.main_head || "-"}</td>
                                      <td className="px-1 py-1">{txn.sub_head || "-"}</td>
                                      <td className="px-1 py-1"><TransactionProofImage path={txn.attachment_path || req.attachment_path} /></td>
                                      <td className="px-1 py-1">{formatDateTime(txn.transaction_created_date || txn.transaction_date)}</td>
                                      <td className="px-1 py-1">{txn.transaction_created_by_name || req.created_by_name || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </motion.div>
          )}

          {canManage && selectedRequest && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
              >
                <h2 className="text-lg font-semibold mb-4">Reject Return Request</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rejection Reason
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter reason for rejection"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction(selectedRequest, "reject", rejectionReason)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                    disabled={!rejectionReason.trim()}
                  >
                    Reject
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {canManage && showConfirmModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
              >
                <h2 className="text-lg font-semibold mb-4">
                  Confirm {showConfirmModal.action === "approve" ? "Approval" : "Rejection"}
                </h2>
                <p className="text-sm mb-4">
                  Are you sure you want to {showConfirmModal.action} this return request?
                  {showConfirmModal.action === "reject" && (
                    <span> Reason: {showConfirmModal.reason}</span>
                  )}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowConfirmModal(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAction}
                    className={`px-4 py-2 text-white rounded-lg text-sm ${
                      showConfirmModal.action === "approve"
                        ? "bg-green-500 hover:bg-green-600"
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
          </div>
        </motion.main>
      </div>
      <div className="fixed bottom-4 right-4">
        <button
          onClick={() => navigate("/Return")}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
        >
          Back to Return Management
        </button>
      </div>
    </div>
  );
};

const styles = `
  .rupee::before {
    content: "₹";
    font-size: 0.9em;
    margin-right: 2px;
  }
  .overflow-x-auto {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .max-h-[400px] {
    max-height: 400px;
    overflow-y: auto;
  }
  .sticky {
    position: sticky;
    top: 0;
    z-index: 10;
  }
  @media (max-width: 640px) {
    .overflow-x-auto {
      max-width: 100vw;
    }
    table {
      min-width: 900px;
      width: auto;
    }
    .border-gradient-to-r {
      border-image: linear-gradient(to right, #4f46e5, #3b82f6) 1;
      padding: 0.25rem;
    }
    input[type="date"], input[type="number"], textarea {
      font-size: 0.75rem;
      padding: 0.5rem;
    }
  }
  @media (min-width: 640px) {
    .border-gradient-to-r {
      border-image: linear-gradient(to right, #4f46e5, #3b82f6) 1;
    }
    input[type="date"], input[type="number"], textarea {
      font-size: 0.875rem;
      padding: 0.5rem;
    }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default AdminReturnRequestReport;
